import { useState, type ReactNode } from "react";
import { ListFilter } from "lucide-react";
import { Button, PageState, SegmentedControl, cn } from "@fox1ck-org/ui";

import { formatDayRange, formatInt } from "@/lib/format";
import { flatten } from "@/lib/report";
import { useT } from "@/lib/i18n";
import { CompareBar, CompareKpis, ReportFilterRow, ReportGrouping, useFilterWords } from "./toolbar";
import { DailyBand } from "./daily-band";
import { ReportTable } from "./report-table";
import { TimeReport } from "./time-report";
import { dimCount } from "./names";
import type { ReportEngine } from "./engine";

export { useReportEngine, type ReportEngine, type ReportEngineConfig } from "./engine";
export { ColumnsButton, CsvButton, LinkButton, ReportFilterRow, ReportGrouping, CompareBar, iconProps } from "./toolbar";
export { ReportTable } from "./report-table";

/**
 * The report under its controls: the band or the comparison tiles, the tree
 * (or «По гео и часам»), the empty and loading states, and the line under it.
 * The page and an embedding screen draw their own header above this.
 */
export function ReportBody({ engine, band = true }: { engine: ReportEngine; band?: boolean }) {
  const t = useT();
  const words = useFilterWords(engine);
  const [bandOpen, setBandOpen] = useState(true);
  const { state, main, tree } = engine;
  const p = state.params;

  if (engine.noCampaigns) {
    return <PageState mode="empty" className="min-h-0 flex-1 rounded-lg border border-border bg-card" title={t("rep.empty.noCampaigns")} description={t("rep.empty.noCampaigns.body")} />;
  }
  if (engine.time) return <TimeReport engine={engine} />;

  const empty = !!main.data && tree.length === 0 && !main.error;
  const filtered = state.filters.dims.length + state.filters.metrics.length > 0;
  const period = formatDayRange(p.from, p.to);
  const conds = [...state.filters.dims.map(words.dimPhrase), ...state.filters.metrics.map(words.metricChip)];
  return (
    <>
      {state.dims[0] === "landing" ? <LanderTypeRow engine={engine} /> : null}
      {engine.compare && state.compareView === "side" ? (
        <CompareKpis engine={engine} />
      ) : band ? (
        <DailyBand engine={engine} open={bandOpen && !empty} onToggle={() => setBandOpen((o) => !o)} />
      ) : null}
      {empty ? (
        <PageState
          mode="empty"
          className="min-h-0 flex-1 rounded-lg border border-border bg-card"
          icon={<ListFilter className="size-5" strokeWidth={1.5} />}
          title={t(filtered ? "rep.empty.filtered" : "rep.empty.period")}
          description={filtered ? emptyFilteredBody(t, period, conds) : t("rep.empty.period.body", { period })}
          action={
            <div className="flex gap-2">
              {filtered ? <Button onClick={() => state.setFilters({ dims: [], metrics: [] })}>{t("rep.empty.reset")}</Button> : null}
              {p.preset !== "30d" ? (
                <Button variant="secondary" onClick={() => p.setPreset("30d")}>
                  {t("rep.empty.30d")}
                </Button>
              ) : null}
            </div>
          }
        />
      ) : (
        <ReportTable engine={engine} className="min-h-0 flex-1" />
      )}
      <ReportFootnote engine={engine} empty={empty} />
    </>
  );
}

/** The line under the report: what is on screen, and what the numbers are. */
/**
 * Why a filtered report is empty, in its own filters' words (доска
 * ReportEmpty): «За 14.10 – 20.10 нет строк, где Страна — Танзания и Профит
 * > 0. Уберите «Профит > 0» или расширьте период.» — the last condition
 * added is the one to drop first. Pure.
 */
export function emptyFilteredBody(t: ReturnType<typeof useT>, period: string, conds: string[]): string {
  if (conds.length === 0) return t("rep.empty.period.body", { period });
  const where = conds.join(` ${t("rep.empty.and")} `);
  return conds.length === 1
    ? t("rep.empty.filtered.one", { period, where })
    : t("rep.empty.filtered.many", { period, where, last: conds[conds.length - 1]! });
}

function ReportFootnote({ engine, empty }: { engine: ReportEngine; empty: boolean }) {
  const t = useT();
  const { main, tree, state } = engine;
  const rows = flatten(tree).length;
  const top = main.data?.total ?? 0;
  let left: ReactNode;
  if (empty) left = t("rep.foot.empty");
  else if (main.isLoading || !main.data) left = t("rep.foot.loading");
  else
    left = (
      <>
        {t("rep.foot.rows", { groups: formatInt(rows), top: dimCount(t, state.dims[0]!, top, engine.dimLabel(state.dims[0]!), formatInt(top)) })}
        {top > tree.length ? ` · ${t("rep.foot.truncated", { shown: formatInt(tree.length) })}` : ""}
      </>
    );
  return (
    <p className="flex shrink-0 items-center gap-4 text-xs text-muted-foreground">
      <span className="min-w-0 flex-1 truncate">{left}</span>
      <span className="shrink-0">{t("rep.foot.legend")}</span>
    </p>
  );
}

/**
 * «Тип: Все · Ленды · Преленды · VSL» over a report by lander (доска
 * ReportLanders). The server has no dimension for the kind of a page, so the
 * choice is sent as «Ленд in …» — the landers of that kind the period has.
 */
function LanderTypeRow({ engine }: { engine: ReportEngine }) {
  const t = useT();
  return (
    <div className="flex shrink-0 items-center gap-3">
      <span className="text-xs text-muted-foreground">{t("rep.lander.type")}</span>
      <SegmentedControl
        size="sm"
        aria-label={t("rep.lander.type")}
        value={engine.state.landerType || "all"}
        onValueChange={(v) => engine.state.setLanderType(v === "all" ? "" : v)}
        options={[
          { value: "all", label: t("rep.lander.all") },
          { value: "landing", label: t("rep.lander.landings") },
          { value: "prelanding", label: t("rep.lander.prelandings") },
          { value: "vsl", label: "VSL" },
        ]}
      />
      <span className="text-xs text-muted-foreground">{t("rep.lander.typeNote")}</span>
      <span className="flex-1" />
      <span className="text-xs text-muted-foreground">{t("rep.lander.crNote")}</span>
    </div>
  );
}

/**
 * The engine with its own controls — what a screen embeds (a campaign's
 * «Отчёт», a lander's «Цифры», an offer): the grouping row, the filters, the
 * actions on the right of the second row, then the body.
 */
export function ReportEngineView({ engine, actions, note, band = false, className }: { engine: ReportEngine; actions?: ReactNode; note?: ReactNode; band?: boolean; className?: string }) {
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-2.5", className)}>
      <ReportGrouping engine={engine} filterTrigger={!engine.state.filters.dims.length && !engine.state.filters.metrics.length && !engine.pinned.length} />
      <ReportFilterRow engine={engine} />
      {actions || note ? (
        <div className="flex min-h-8 shrink-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{note}</span>
          {actions}
        </div>
      ) : null}
      <CompareBar engine={engine} />
      <ReportBody engine={engine} band={band} />
    </div>
  );
}
